import { Link, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import { getArtwork, postEnquiry, createCheckout } from '@/lib/api';
import StructuredData, { artworkLd } from '@/components/StructuredData';
import PictureImg from '@/components/PictureImg';

export default function ArtworkDetail() {
    const { slug } = useParams();
    const reveal = useReveal();
    const [data, setData] = useState(null);
    const [zoom, setZoom] = useState(false);
    const [drawer, setDrawer] = useState(false);
    const [purchase, setPurchase] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
    const [buyer, setBuyer] = useState({ name: '', email: '', phone: '' });
    const [buyerError, setBuyerError] = useState('');
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const enquiryRef = useRef(null);
    const purchaseRef = useRef(null);
    const enquiryCloseRef = useRef(null);
    const purchaseCloseRef = useRef(null);

    useEffect(() => { getArtwork(slug).then(setData); }, [slug]);

    /* When a drawer opens: pause Lenis so it stops eating wheel deltas, lock
       the body scroll so the background stays put, and move keyboard focus
       into the drawer. Reverse everything on close. This resolves the
       "click-once-to-activate-scroll" bug reported for the enquire /
       reserve panels on both desktop and mobile. */
    useEffect(() => {
        const isOpen = drawer || purchase;
        const lenis = typeof window !== 'undefined' ? window.__lenis : null;
        if (isOpen) {
            if (lenis && lenis.stop) lenis.stop();
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            const focusTarget = drawer ? enquiryCloseRef.current : purchaseCloseRef.current;
            const panel = drawer ? enquiryRef.current : purchaseRef.current;
            /* Focus the close button so the panel is immediately keyboard-
               active AND (crucially) so subsequent wheel/touch events on the
               panel route to the panel's own scroll container without any
               extra click. */
            requestAnimationFrame(() => {
                if (focusTarget) focusTarget.focus({ preventScroll: true });
                if (panel) panel.scrollTop = 0;
            });
            return () => {
                const savedY = -parseInt(document.body.style.top || '0', 10) || 0;
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';
                document.body.style.width = '';
                window.scrollTo(0, savedY);
                if (lenis && lenis.start) lenis.start();
            };
        }
    }, [drawer, purchase]);

    /* ESC closes any open drawer */
    useEffect(() => {
        if (!drawer && !purchase) return;
        const onKey = (e) => {
            if (e.key === 'Escape') { setDrawer(false); setPurchase(false); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawer, purchase]);

    useDocumentMeta({
        title: data ? `${data.artwork.title}, ${data.artwork.year} · ${data.artwork.artist_name} · Maison Touré` : 'Artwork · Maison Touré',
        description: data ? `${data.artwork.artist_name} — ${data.artwork.title}, ${data.artwork.year}. ${data.artwork.medium}. ${data.artwork.dimensions}.` : undefined,
    });

    if (!data) {
        return (
            <div style={{ paddingTop: 200 }} className="max-w-[1600px] mx-auto px-8 md:px-16">
                <p className="serif-italic" style={{ fontSize: 22, color: 'var(--ink-2)' }}>Loading…</p>
            </div>
        );
    }
    const { artwork, artist, related } = data;

    const submitEnquiry = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await postEnquiry({
                ...form,
                type: 'artwork',
                artwork_slug: artwork.slug,
                artwork_title: artwork.title,
            });
            setSent(true);
        } finally { setBusy(false); }
    };

    const startCheckout = async (payment_type) => {
        setBuyerError('');
        const name = buyer.name.trim();
        const email = buyer.email.trim();
        if (!name || !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setBuyerError('Please enter your name and email before continuing.');
            return;
        }
        setBusy(true);
        try {
            const res = await createCheckout({
                artwork_slug: artwork.slug,
                payment_type,
                origin_url: window.location.origin,
                customer_name: name,
                customer_email: email,
                customer_phone: buyer.phone.trim() || undefined,
            });
            window.location.href = res.url;
        } catch (err) {
            alert(err?.response?.data?.detail || 'Unable to start checkout.');
            setBusy(false);
        }
    };

    /* Catalogue rows — museum convention */
    const cat = [
        ['Artist', artwork.artist_name],
        ['Title', `${artwork.title}, ${artwork.year}`],
        ['Medium', artwork.medium],
        ['Dimensions', artwork.dimensions],
        ['Edition', artwork.edition || 'Unique'],
        artwork.series ? ['Series', artwork.series] : null,
        ['Provenance', 'The artist; Maison Touré, Sydney'],
        ['Documentation', 'Certificate of authenticity & condition report'],
        ['Availability', artwork.availability],
        ['Price', artwork.price_on_request ? 'On request' : `${artwork.currency} ${artwork.price?.toLocaleString()}`],
    ].filter(Boolean);

    return (
        <div ref={reveal} data-testid="artwork-detail-page" style={{ paddingTop: 144 }}>
            <StructuredData data={artworkLd(artwork)} />
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-8">
                <Link to="/artworks" className="overline" style={{ color: 'var(--ink-3)' }}>The Collection</Link>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
                    {/* IMAGE — generous, no fixed crop */}
                    <div className="md:col-span-8 reveal">
                        <button
                            data-testid="artwork-zoom-trigger"
                            onClick={() => setZoom(true)}
                            onContextMenu={(e) => e.preventDefault()}
                            className="img-wrap block w-full"
                            style={{ background: 'var(--bg-3)', cursor: 'zoom-in', aspectRatio: 'auto' }}
                        >
                            <PictureImg
                                src={artwork.image}
                                alt={`${artwork.artist_name} — ${artwork.title}, ${artwork.year}`}
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                                className="no-save"
                            />
                        </button>
                        <div className="mt-5 overline" style={{ color: 'var(--ink-3)' }}>Click image to view full scale</div>
                    </div>

                    {/* CATALOGUE PANEL */}
                    <div className="md:col-span-4 reveal flex flex-col">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>{artwork.availability}</span>

                        <div
                            className="mt-8"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontStyle: 'italic',
                                fontSize: 20,
                                color: 'var(--ink-2)',
                            }}
                        >
                            <Link to={`/artists/${artist.slug}`} className="hover:underline">
                                {artwork.artist_name}
                            </Link>
                        </div>
                        <h1
                            className="mt-3"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontStyle: 'italic',
                                fontWeight: 500,
                                fontSize: 'clamp(36px, 3.6vw, 56px)',
                                lineHeight: 1.06,
                                letterSpacing: '-0.018em',
                            }}
                        >
                            {artwork.title},{' '}
                            <span style={{ color: 'var(--ink-3)' }}>{artwork.year}</span>
                        </h1>

                        <p
                            className="mt-10"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 17,
                                lineHeight: 1.7,
                                color: 'var(--ink-2)',
                            }}
                        >
                            {artwork.description}
                        </p>

                        {/* Catalogue dl */}
                        <dl className="mt-12 flex flex-col">
                            {cat.map(([k, v]) => (
                                <div key={k} className="grid grid-cols-3 gap-6 py-4 rule-bottom">
                                    <dt className="overline" style={{ color: 'var(--ink-3)' }}>{k}</dt>
                                    <dd
                                        className="col-span-2"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontWeight: 400,
                                            fontSize: 16,
                                            lineHeight: 1.45,
                                            color: 'var(--ink)',
                                        }}
                                    >
                                        {v}
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        {/* Actions */}
                        <div className="mt-12 flex flex-col items-start gap-6">
                            <button
                                data-testid="artwork-enquire-btn"
                                onClick={() => setDrawer(true)}
                                className="btn-primary"
                            >
                                Enquire about this work
                            </button>
                            {!artwork.price_on_request && artwork.availability === 'Available' && (
                                <button
                                    data-testid="artwork-acquire-btn"
                                    onClick={() => setPurchase(true)}
                                    className="btn-ghost"
                                >
                                    Acquire or place a 10% reserve
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ARTIST BIO */}
            {artist && (
                <section className="rule-top mt-32 md:mt-48" data-testid="artwork-artist-bio">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
                        <div className="md:col-span-4 reveal">
                            <div className="img-wrap aspect-[3/4]" style={{ background: 'var(--bg-3)' }}>
                                <PictureImg src={artist.portrait} alt={artist.name} loading="lazy" />
                            </div>
                        </div>
                        <div className="md:col-span-7 md:col-start-6 reveal">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>The Artist</span>
                            <h2
                                className="mt-6"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontWeight: 400,
                                    fontSize: 'clamp(36px, 4.4vw, 64px)',
                                    lineHeight: 1.04,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                {artist.name}
                            </h2>
                            <div
                                className="mt-3"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontStyle: 'italic',
                                    fontSize: 18,
                                    color: 'var(--ink-2)',
                                }}
                            >
                                {artist.origin} &nbsp;·&nbsp; {artist.born}
                            </div>
                            <p
                                className="mt-8"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontWeight: 400,
                                    fontSize: 17,
                                    lineHeight: 1.75,
                                    color: 'var(--ink-2)',
                                }}
                            >
                                {artist.biography}
                            </p>
                            <Link to={`/artists/${artist.slug}`} className="btn-ghost mt-10 inline-flex">
                                Artist profile
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* RELATED WORKS */}
            {related?.length > 0 && (
                <section className="rule-top" data-testid="artwork-related">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                        <header className="reveal mb-20 md:mb-24">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>Further Works</span>
                            <h2
                                className="serif mt-6"
                                style={{ fontSize: 'clamp(32px, 4vw, 60px)', letterSpacing: '-0.018em', lineHeight: 1.06 }}
                            >
                                Other works by <em className="serif-italic">{artist.name}.</em>
                            </h2>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-16">
                            {related.slice(0, 3).map((w, i) => (
                                <Link
                                    key={w.slug}
                                    to={`/artworks/${w.slug}`}
                                    className={`reveal group block ${i === 1 ? 'md:mt-14' : ''}`}
                                >
                                    <div className="img-wrap aspect-[4/5]" style={{ background: 'var(--bg-3)' }} onContextMenu={(e) => e.preventDefault()}>
                                        <PictureImg src={w.image} alt={w.title} loading="lazy" className="no-save" />
                                    </div>
                                    <h4
                                        className="mt-6"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontWeight: 500,
                                            fontSize: 'clamp(20px, 2vw, 28px)',
                                            lineHeight: 1.16,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {w.title}, <span style={{ color: 'var(--ink-3)' }}>{w.year}</span>
                                    </h4>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ZOOM MODAL */}
            {zoom && (
                <div data-testid="artwork-zoom-modal" className="zoom-stage" onClick={() => setZoom(false)} onContextMenu={(e) => e.preventDefault()}>
                    <PictureImg src={artwork.image} alt={artwork.title} className="no-save" />
                </div>
            )}

            {/* ENQUIRY DRAWER */}
            <div className={`drawer-backdrop ${drawer || purchase ? 'open' : ''}`} onClick={() => { setDrawer(false); setPurchase(false); }} />
            <aside
                ref={enquiryRef}
                data-lenis-prevent
                aria-hidden={!drawer}
                role="dialog"
                aria-modal={drawer}
                aria-labelledby="enquiry-drawer-title"
                className={`drawer ${drawer ? 'open' : ''}`}
                data-testid="enquiry-drawer"
            >
                <div className="p-10 md:p-16">
                    <button ref={enquiryCloseRef} onClick={() => setDrawer(false)} className="overline" data-testid="drawer-close" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>Close</button>
                    {!sent ? (
                        <>
                            <h3
                                className="mt-12"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontWeight: 400,
                                    fontSize: 'clamp(36px, 3.6vw, 56px)',
                                    lineHeight: 1.04,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                <em className="serif-italic" style={{ fontWeight: 500 }}>Enquire.</em>
                            </h3>
                            <p className="mt-4 serif-italic" style={{ fontSize: 18, color: 'var(--ink-2)' }}>
                                {artwork.title}, {artwork.year} &nbsp;—&nbsp; {artwork.artist_name}
                            </p>
                            <form onSubmit={submitEnquiry} className="mt-14 flex flex-col gap-7">
                                <input data-testid="enquiry-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-line" />
                                <input data-testid="enquiry-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-line" />
                                <input data-testid="enquiry-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-line" />
                                <textarea data-testid="enquiry-message" required rows={4} placeholder="Your message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-line" />
                                <button data-testid="enquiry-submit" disabled={busy} className="btn-primary self-start mt-6">{busy ? 'Sending' : 'Send enquiry'}</button>
                            </form>
                        </>
                    ) : (
                        <div data-testid="enquiry-success" className="mt-24">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>Received</span>
                            <h3
                                className="mt-6"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontWeight: 400,
                                    fontSize: 'clamp(32px, 3.4vw, 52px)',
                                    lineHeight: 1.08,
                                    letterSpacing: '-0.018em',
                                }}
                            >
                                Your enquiry has been <em className="serif-italic">received.</em>
                            </h3>
                            <p className="mt-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, lineHeight: 1.7, color: 'var(--ink-2)' }}>
                                A member of the Maison Touré team will write to you within one business day.
                            </p>
                        </div>
                    )}
                </div>
            </aside>

            {/* PURCHASE DRAWER */}
            <aside
                ref={purchaseRef}
                data-lenis-prevent
                aria-hidden={!purchase}
                role="dialog"
                aria-modal={purchase}
                aria-labelledby="purchase-drawer-title"
                className={`drawer ${purchase ? 'open' : ''}`}
                data-testid="purchase-drawer"
            >
                <div className="p-10 md:p-16">
                    <button ref={purchaseCloseRef} onClick={() => setPurchase(false)} className="overline" data-testid="purchase-close" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>Close</button>
                    <h3
                        className="mt-12"
                        style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            fontWeight: 400,
                            fontSize: 'clamp(36px, 3.6vw, 56px)',
                            lineHeight: 1.04,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        <em className="serif-italic" style={{ fontWeight: 500 }}>Acquire.</em>
                    </h3>
                    <p className="mt-4 serif-italic" style={{ fontSize: 18, color: 'var(--ink-2)' }}>{artwork.title}, {artwork.year}</p>

                    <div className="mt-14 flex flex-col gap-8">
                        <input
                            data-testid="buyer-name"
                            className="input-line"
                            placeholder="Full name"
                            autoComplete="name"
                            value={buyer.name}
                            onChange={(e) => { setBuyer({ ...buyer, name: e.target.value }); setBuyerError(''); }}
                        />
                        <input
                            data-testid="buyer-email"
                            type="email"
                            className="input-line"
                            placeholder="Email"
                            autoComplete="email"
                            value={buyer.email}
                            onChange={(e) => { setBuyer({ ...buyer, email: e.target.value }); setBuyerError(''); }}
                        />
                        <input
                            data-testid="buyer-phone"
                            className="input-line"
                            placeholder="Phone (optional)"
                            autoComplete="tel"
                            value={buyer.phone}
                            onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })}
                        />
                        {buyerError && (
                            <div
                                data-testid="buyer-error"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontStyle: 'italic',
                                    fontSize: 14,
                                    color: 'var(--ochre)',
                                }}
                            >
                                {buyerError}
                            </div>
                        )}
                    </div>

                    <div className="mt-14 flex flex-col gap-12">
                        <div className="rule-bottom pb-10">
                            <span className="overline" style={{ color: 'var(--ink-3)' }}>I &nbsp;—&nbsp; Reserve</span>
                            <h4
                                className="mt-4"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontStyle: 'italic',
                                    fontWeight: 500,
                                    fontSize: 28,
                                    lineHeight: 1.16,
                                }}
                            >
                                Place a 10% reserve.
                            </h4>
                            <p className="mt-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, lineHeight: 1.65, color: 'var(--ink-2)' }}>
                                A refundable deposit holds the work for fourteen days while we coordinate viewing and provenance documentation.
                            </p>
                            <div className="mt-6 flex justify-between items-end">
                                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22 }}>
                                    {artwork.currency} {(artwork.price * 0.1).toLocaleString()}
                                </div>
                                <button data-testid="checkout-deposit-btn" disabled={busy} onClick={() => startCheckout('deposit')} className="btn-ghost">
                                    Reserve
                                </button>
                            </div>
                        </div>
                        <div>
                            <span className="overline" style={{ color: 'var(--ink-3)' }}>II &nbsp;—&nbsp; Acquire</span>
                            <h4
                                className="mt-4"
                                style={{
                                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                                    fontStyle: 'italic',
                                    fontWeight: 500,
                                    fontSize: 28,
                                    lineHeight: 1.16,
                                }}
                            >
                                Acquire in full.
                            </h4>
                            <p className="mt-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, lineHeight: 1.65, color: 'var(--ink-2)' }}>
                                Secure payment via Stripe. White-glove handling and worldwide delivery arranged thereafter.
                            </p>
                            <div className="mt-6 flex justify-between items-end">
                                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22 }}>
                                    {artwork.currency} {artwork.price?.toLocaleString()}
                                </div>
                                <button data-testid="checkout-full-btn" disabled={busy} onClick={() => startCheckout('full')} className="btn-primary">
                                    Proceed
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>
        </div>
    );
}
